import { Document, Font, Image, Page, pdf, StyleSheet, Text, View } from '@react-pdf/renderer';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box2, Vector2, Vector3 } from 'three';

import { Tile } from '../../../../../../../../typings/api/skymap/rest/v0/orthotile';
import montserratBoldUrl from '../../../../../../../static/fonts/truetype/Montserrat-Bold.ttf';
import montserratRegularUrl from '../../../../../../../static/fonts/truetype/Montserrat-Regular.ttf';
import defaultLogoUrl from '../../../../../../../static/themes/default/images/logotype.png';
import mobaLogoUrl from '../../../../../../../static/themes/moba/images/logotype.png';
import { DynamicAssetService } from '../../../../../../services/axios/services/v0/dynamic-asset-service';
import { TerrainModelService } from '../../../../../../services/axios/services/v0/terrain-model-service';
import { SkyMapAxiosServiceFactory } from '../../../../../../services/axios/skymap-axios-service-factory';
import { WebApi } from '../../../../../../services/web-api';
import { CoordinateManager } from '../../../../../../viewer/coordinate-manager';
import { TerrainModel } from '../../../../../../viewer/elements/terrain-model';
import { CellGridVolume } from '../../../../../../viewer/elements/volume/cell-grid-volume';
import { CellGridVolumeModel } from '../../../../../../viewer/elements/volume/model/cell-grid-volume-model';
import { VolumeCalculationAsset } from '../../../../../../viewer/elements/volume/types';
import { ProjectGeodataManager } from '../../../../../../viewer/project-geodata-manager';
import { GlobalMercator } from '../../../../../../viewer/utilities/projections/global-mercator';
import { ViewerEnvironmentInstance } from '../../../../../../viewer/viewer-environment';
import { ThemeManager } from '../../../../../themes/theme-manager';
import { ProgressController } from '../../../../../utils/progress-controller';
import { pdfPageDimensions } from '../helpers/types';
import {
  downloadBlob,
  getFormatedDate,
  getVolumeData,
  VolumeReportData,
  VolumeWithAsset,
} from '../helpers/utils';

Font.register({
  family: 'Montserrat',
  fonts: [
    { src: montserratRegularUrl, fontWeight: 'normal' },
    { src: montserratBoldUrl, fontWeight: 'bold' },
  ],
});

// --- Canvas-based overview image generation (unchanged from original) ---

export interface VolumeRenderingData {
  pngData: Blob;
  extents: Box2;
  gsd: number;
}

export const getBubbleExtents = (pos: Vector2, textWidth: number, textHeight: number) => {
  const size = new Vector2(textHeight / 2, textWidth / 2);
  return new Box2(new Vector2(pos.x, pos.y).sub(size), new Vector2(pos.x, pos.y).add(size));
};

export const getOffset = (
  pos: Vector2,
  baseBubbleOffset: Vector2,
  context: CanvasRenderingContext2D,
  volumeRenderingData: VolumeRenderingData[],
  renderedBubbleExtents: Box2[],
  textWidth: number,
  textHeight: number,
) => {
  const anglePoints: { angle: number; score: number; extents: Box2 }[] = [];

  for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 8) {
    const angledOffset = new Vector2(baseBubbleOffset.x, baseBubbleOffset.y).rotateAround(
      new Vector2(),
      angle,
    );

    const offsetPosition = new Vector2().addVectors(pos, angledOffset);
    const extents = getBubbleExtents(offsetPosition, textWidth, textHeight);
    let score = 0;

    if (
      offsetPosition.x < textWidth ||
      offsetPosition.x > context.canvas.width - textWidth * 2 ||
      offsetPosition.y < textHeight ||
      offsetPosition.y > context.canvas.height - textHeight * 2
    ) {
      continue;
    }

    for (const bubble of renderedBubbleExtents) {
      if (extents.intersectsBox(bubble)) {
        score += 100;
        break;
      }
    }

    for (const volumeData of volumeRenderingData) {
      if (extents.intersectsBox(volumeData.extents)) {
        score += 1;
      }
    }

    anglePoints.push({ angle, score, extents });
  }

  if (anglePoints.length === 0) {
    return new Vector2(0, 0);
  }

  const bestAngle = anglePoints.sort((a, b) => a.score - b.score)[0];
  renderedBubbleExtents.push(bestAngle.extents);
  return new Vector2(baseBubbleOffset.x, baseBubbleOffset.y).rotateAround(
    new Vector2(),
    bestAngle.angle,
  );
};

const drawLineOnCanvas = (start: Vector2, end: Vector2, context: CanvasRenderingContext2D) => {
  context.beginPath();
  context.moveTo(start.x, start.y);
  context.lineTo(end.x, end.y);
  context.stroke();
};

const drawTextBubbleOnCanvas = (
  text: string,
  pos: Vector2,
  context: CanvasRenderingContext2D,
  textWidth: number,
  textHeight: number,
) => {
  context.fillStyle = `rgb(255,255,255)`;
  context.beginPath();
  context.arc(pos.x, pos.y, textHeight / 1.5, 0, 2 * Math.PI);
  context.fill();
  context.fillStyle = `rgb(0,0,0)`;
  context.fillText(text, pos.x - textWidth / 2, pos.y + textHeight / 3);
};

const drawNumberLabelsOnCanvas = (
  context: CanvasRenderingContext2D,
  canvasPosition: Vector2,
  gsd: number,
  volumeRenderingData: VolumeRenderingData[],
) => {
  const textHeight = context.canvas.height / 20;
  const baseBubbleOffset = new Vector2(0, -context.canvas.height / 10);

  context.font = `${textHeight}px Montserrat`;
  context.strokeStyle = `rgb(255,255,255)`;

  const renderedBubbleExtents: Box2[] = [];

  for (let i = 0; i < volumeRenderingData.length; i++) {
    const pos = volumeRenderingData[i].extents
      .getCenter(new Vector2())
      .sub(canvasPosition)
      .multiply(new Vector2(1, -1))
      .divideScalar(gsd);

    const text = (i + 1).toString();
    const textWidth = context.measureText(text).width;

    const offset = getOffset(
      pos,
      baseBubbleOffset,
      context,
      volumeRenderingData,
      renderedBubbleExtents,
      textWidth,
      textHeight,
    );
    const offsetPosition = new Vector2().addVectors(pos, offset);

    drawLineOnCanvas(pos, offsetPosition, context);
    drawTextBubbleOnCanvas(text, offsetPosition, context, textWidth, textHeight);
  }
};

const drawVolumeOnCanvas = async (
  context: CanvasRenderingContext2D,
  volume: VolumeRenderingData,
  canvasPosition: Vector2,
  gsd: number,
) => {
  const volumePosition = new Vector2(volume.extents.min.x, volume.extents.max.y);
  const pos = new Vector2().subVectors(volumePosition, canvasPosition).divideScalar(gsd);
  return new Promise((resolve, reject) => {
    const image = document.getElementById('volumeReportImg') as HTMLImageElement;
    image.onload = () => {
      context.drawImage(
        image,
        pos.x,
        -pos.y,
        (image.width * volume.gsd) / gsd,
        (image.height * volume.gsd) / gsd,
      );
      resolve(0);
    };
    image.src = URL.createObjectURL(volume.pngData);
  });
};

const iteratePoints = (
  points: Float32Array,
  extents: Box2,
  currentVolumeExtents: Box2,
  offset: Vector3,
) => {
  for (let i = 0; i < points.length; i += 3) {
    const point = new Vector2(points[i] + offset.x, points[i + 1] + offset.y);
    extents.expandByPoint(point);
    currentVolumeExtents.expandByPoint(point);
  }
};

const drawPixelVolumeImageData = (
  x: number,
  y: number,
  canvas: HTMLCanvasElement,
  volumeImageData: ImageData,
) => {
  const index = (x + y * canvas.width) * 4;
  volumeImageData.data[index + 0] = 254; // r
  volumeImageData.data[index + 1] = 213; // g
  volumeImageData.data[index + 2] = 48; // b
  volumeImageData.data[index + 3] = 120; // a
};

const fetchChunksFromUrls = async (
  urls: string[],
  extents: Box2,
  currentVolumeExtents: Box2,
  offset: Vector3,
) => {
  const chunks: Float32Array[] = [];
  for (const url of urls) {
    const volumeData = await CellGridVolumeModel.fetchVolumeChunk(url);

    const cutPoints = volumeData.cutData.vertices;
    const fillPoints = volumeData.fillData.vertices;

    iteratePoints(cutPoints, extents, currentVolumeExtents, offset);
    iteratePoints(fillPoints, extents, currentVolumeExtents, offset);

    chunks.push(cutPoints);
    chunks.push(fillPoints);
  }
  return chunks;
};

const generateVolumeRenderingData = async (
  asset: VolumeCalculationAsset,
  volume: CellGridVolume,
  extents: Box2,
  dynamicAssetService: DynamicAssetService,
  terrainModelService: TerrainModelService,
): Promise<VolumeRenderingData> => {
  if (!volume.offset) {
    throw new Error('volume.offset must be defined');
  }

  const canvas = document.getElementById('volumeReportCanvas') as HTMLCanvasElement;

  const response =
    asset instanceof TerrainModel
      ? await terrainModelService.fetchVolumeModelUrls({
          path: { id: asset.geoDataId, volumeId: volume.id! },
        })
      : await dynamicAssetService.fetchVolumeModelUrls({
          path: { assetId: asset.assetId!, volumeId: volume.id! },
        });

  const currentVolumeExtents = new Box2(
    new Vector2(Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER),
    new Vector2(Number.MIN_SAFE_INTEGER, Number.MIN_SAFE_INTEGER),
  );

  const chunks = await fetchChunksFromUrls(
    response.urls,
    extents,
    currentVolumeExtents,
    volume.offset,
  );

  const size = currentVolumeExtents.getSize(new Vector2()).divideScalar(0.1);
  const maxSize = 256;

  if (size.width > maxSize) {
    size.multiplyScalar(maxSize / size.width);
  }
  if (size.height > maxSize) {
    size.multiplyScalar(maxSize / size.width);
  }

  const volumeGsd = currentVolumeExtents.getSize(new Vector2()).width / size.width;
  const volumePosition = new Vector2(currentVolumeExtents.min.x, currentVolumeExtents.max.y);
  canvas.width = Math.ceil(size.width);
  canvas.height = Math.ceil(size.height);
  const context = canvas.getContext('2d')!;
  const volumeImageData = new ImageData(canvas.width, canvas.height);

  for (const arr of chunks) {
    for (let i = 0; i < arr.length; i += 3) {
      const x = Math.round((arr[i] + volume.offset.x - volumePosition.x) / volumeGsd);
      const y = Math.round(-(arr[i + 1] + volume.offset.y - volumePosition.y) / volumeGsd);

      if (x >= volumeImageData.width || y >= volumeImageData.height || x < 0 || y < 0) {
        continue;
      }

      drawPixelVolumeImageData(x, y, canvas, volumeImageData);
    }
  }

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.putImageData(volumeImageData, 0, 0);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject;
        return;
      }

      resolve({
        pngData: blob,
        extents: currentVolumeExtents,
        gsd: volumeGsd,
      });
    }, 'image/png');
  });
};

const getSwerefExtents = (tile: Tile, globalMercator: GlobalMercator) => {
  const tileBounds = globalMercator.tileLatLonBounds(tile.column, tile.row, tile.zoom);
  const projection = CoordinateManager.getInstance().getProjectProjection();
  const sweref99 = {
    se: CoordinateManager.getInstance().convertCoords(
      tileBounds.east,
      tileBounds.south,
      'wgs84',
      projection,
    ),
    sw: CoordinateManager.getInstance().convertCoords(
      tileBounds.west,
      tileBounds.south,
      'wgs84',
      projection,
    ),
    ne: CoordinateManager.getInstance().convertCoords(
      tileBounds.east,
      tileBounds.north,
      'wgs84',
      projection,
    ),
    nw: CoordinateManager.getInstance().convertCoords(
      tileBounds.west,
      tileBounds.north,
      'wgs84',
      projection,
    ),
  };
  const box = new Box2(sweref99.sw, sweref99.ne);
  box.expandByPoint(sweref99.se);
  box.expandByPoint(sweref99.nw);
  return box;
};

const drawGoogleTileOnCanvas = async (
  context: CanvasRenderingContext2D,
  orthotileId: string,
  currentTile: Tile,
  offset: Vector2,
  farOffset: Vector2,
) => {
  const { url: tileS3URL } = await SkyMapAxiosServiceFactory.instance
    .createOrthoTileServiceV0()
    .fetchTile({ body: { orthotileName: orthotileId, tile: currentTile } });

  try {
    const arrayBuffer = await WebApi.fetchS3DataAsArrayBuffer(tileS3URL);
    const bitmap = await createImageBitmap(new Blob([arrayBuffer], { type: 'image/png' }));
    const dx = Math.floor(offset.x);
    const dy = Math.floor(-offset.y);
    const dw = Math.ceil(Math.abs(farOffset.x - offset.x));
    const dh = Math.ceil(Math.abs(farOffset.y - offset.y));
    context.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height, dx, dy, dw, dh);
    bitmap.close();
  } catch (err: any) {
    if (err?.code === 404) {
      return;
    }
    throw new Error('Failed drawing google tile to canvas');
  }
};

const generateOverview = async (
  selectedVolumes: VolumeWithAsset[],
  dynamicAssetService: DynamicAssetService,
  terrainModelService: TerrainModelService,
  progressController?: ProgressController,
): Promise<ArrayBuffer | undefined> => {
  const orthoData = ProjectGeodataManager.instance().getActivOrthotileGeodata();
  // The overview is stitched from XYZ tiles, which COG orthotiles don't have
  const orthotile = orthoData?.format === 'XYZ' ? orthoData.model : undefined;
  const geodataId = orthotile?.parentGeodataId;

  const volumeRenderingData: VolumeRenderingData[] = [];
  const overViewExtents = new Box2(
    new Vector2(Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER),
    new Vector2(Number.MIN_SAFE_INTEGER, Number.MIN_SAFE_INTEGER),
  );

  if (!(orthotile && geodataId)) {
    return;
  }

  const globalMercator = new GlobalMercator(orthotile.tileSize);
  let gsd = 0.2;

  for (const item of selectedVolumes) {
    if (item.volume && item.volume instanceof CellGridVolume) {
      volumeRenderingData.push(
        await generateVolumeRenderingData(
          item.asset,
          item.volume,
          overViewExtents,
          dynamicAssetService,
          terrainModelService,
        ),
      );
    }
    progressController?.next();
  }

  overViewExtents.expandByVector(new Vector2(10, 10));
  const canvasPosition = new Vector2(overViewExtents.min.x, overViewExtents.max.y);
  const canvas = document.getElementById('volumeReportCanvas') as HTMLCanvasElement;
  const context = canvas.getContext('2d')!;

  let lodLevel = 0;
  let tileSizeMeter = globalMercator.tileToMeterWidth(orthotile.levels[lodLevel].zoom);

  for (
    ;
    lodLevel < orthotile.levels.length - 1 &&
    tileSizeMeter > overViewExtents.max.x - overViewExtents.min.x;
    lodLevel++
  ) {
    tileSizeMeter = globalMercator.tileToMeterWidth(orthotile.levels[lodLevel + 1].zoom);
  }

  const level = orthotile.levels[lodLevel];
  const topLeftTile = { column: level.columnMin, row: level.rowMax, zoom: level.zoom };
  const layerBaseTileExtents = getSwerefExtents(topLeftTile, globalMercator);
  const tileWidthMeters = layerBaseTileExtents.max.x - layerBaseTileExtents.min.x;

  gsd = tileWidthMeters / (orthotile.tileSize / 2);
  canvas.width = (overViewExtents.max.x - overViewExtents.min.x) / gsd;
  canvas.height = (overViewExtents.max.y - overViewExtents.min.y) / gsd;

  for (let x = level.columnMin; x <= level.columnMax; x++) {
    for (let y = level.rowMin; y <= level.rowMax; y++) {
      const currentTile = { column: x, row: y, zoom: topLeftTile.zoom };
      const sweref = getSwerefExtents(currentTile, globalMercator);
      if (overViewExtents.intersectsBox(sweref)) {
        const offset = new Vector2(sweref.min.x, sweref.max.y)
          .sub(canvasPosition)
          .divideScalar(gsd);
        const farOffset = new Vector2(sweref.max.x, sweref.min.y)
          .sub(canvasPosition)
          .divideScalar(gsd);
        await drawGoogleTileOnCanvas(context, orthotile.id, currentTile, offset, farOffset);
      }
    }
  }

  for (const volume of volumeRenderingData) {
    await drawVolumeOnCanvas(context, volume, canvasPosition, gsd);
  }

  drawNumberLabelsOnCanvas(context, canvasPosition, gsd, volumeRenderingData);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob?.arrayBuffer());
    }, 'image/png');
  });
};

// --- React-PDF components ---

const TableRow = ({
  columns,
  bold,
}: {
  columns: [string, string, string, string];
  bold?: boolean;
}) => (
  <View style={bold ? [tableStyles.row, tableStyles.boldRow] : tableStyles.row}>
    <Text style={tableStyles.col1}>{columns[0]}</Text>
    <Text style={tableStyles.col2}>{columns[1]}</Text>
    <Text style={tableStyles.col3}>{columns[2]}</Text>
    <Text style={tableStyles.col4}>{columns[3]}</Text>
  </View>
);

const VolumeSection = ({ index, volume }: { index: number; volume: VolumeReportData }) => {
  const { t } = useTranslation();
  const isCellGrid = volume.type === 'cell_grid';
  const unitSymbols = ViewerEnvironmentInstance.getUnitSymbols();

  return (
    <View style={tableStyles.volumeBlock} wrap={false}>
      <TableRow columns={[(index + 1).toString(), volume.name, '', '']} />
      <TableRow
        bold={true}
        columns={[
          '',
          t('common.targetSurface', { ns: 'skyviewVolumeCalculation' }),
          t('common.cut', { ns: 'skyviewVolumeCalculation' }),
          t('common.fill', { ns: 'skyviewVolumeCalculation' }),
        ]}
      />
      <TableRow
        columns={[
          '',
          volume.targetAssetName,
          `${volume.cut.toFixed(2)} ${unitSymbols.volume}`,
          `${volume.fill.toFixed(2)} ${unitSymbols.volume}`,
        ]}
      />
      <TableRow
        bold={true}
        columns={[
          '',
          t('common.sourceSurface', { ns: 'skyviewVolumeCalculation' }),
          isCellGrid ? '' : t('common.surface2d', { ns: 'skyviewVolumeCalculation' }),
          isCellGrid ? '' : t('common.surface3d', { ns: 'skyviewVolumeCalculation' }),
        ]}
      />
      <TableRow
        columns={[
          '',
          volume.sourceName ?? '',
          isCellGrid
            ? ''
            : `${(volume.cutPlanArea + volume.fillPlanArea).toFixed(2)} ${unitSymbols.area}`,
          isCellGrid ? '' : `${(volume.cutArea + volume.fillArea).toFixed(2)} ${unitSymbols.area}`,
        ]}
      />
    </View>
  );
};

type Props = {
  logoUrl: string;
  overviewImageUrl?: string;
  projectName: string;
  reportName: string;
  dateString: string;
  selectedVolumes: VolumeWithAsset[];
};

const VolumePdfReport = (props: Props) => {
  const { t } = useTranslation();
  const volumeData = getVolumeData(props.selectedVolumes);

  return (
    <Document>
      {/* Cover page: header + overview fills remaining space */}
      <Page dpi={72} size="A4" style={styles.coverPage}>
        <Image src={props.logoUrl} style={styles.logo} />
        <Text style={styles.title}>{props.reportName}</Text>
        <Text style={styles.subtitle}>
          {t('export.projectName', { ns: 'skyviewReport', projectName: props.projectName })}
        </Text>
        <Text style={styles.subtitle}>
          {t('export.reportCreated', {
            ns: 'skyviewReport',
            dateTime: props.dateString,
            interpolation: { escapeValue: false },
          })}
        </Text>
        {props.overviewImageUrl && (
          <View style={styles.overviewContainer}>
            <Image src={props.overviewImageUrl} style={styles.overviewImage} />
          </View>
        )}
      </Page>

      {/* Volume data table always starts on its own page */}
      <Page dpi={72} size="A4" style={styles.page}>
        <TableRow
          bold={true}
          columns={[
            t('export.number', { ns: 'skyviewReport' }),
            t('export.name', { ns: 'skyviewReport' }),
            '',
            '',
          ]}
        />
        {volumeData.map((volume, i) => (
          <VolumeSection index={i} key={i} volume={volume} />
        ))}
      </Page>
    </Document>
  );
};

const { defaultPadding } = pdfPageDimensions.a4.portrait72Dpi;

const styles = StyleSheet.create({
  coverPage: {
    padding: defaultPadding,
    fontFamily: 'Montserrat',
    fontSize: 12,
    flexDirection: 'column',
  },
  page: {
    padding: defaultPadding,
    fontFamily: 'Montserrat',
    fontSize: 12,
  },
  logo: {
    width: 120,
    marginBottom: 14,
  },
  title: {
    fontSize: 25,
    marginBottom: 14,
  },
  subtitle: {
    fontSize: 14,
    marginBottom: 2,
  },
  overviewContainer: {
    flex: 1,
    marginTop: 20,
  },
  overviewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
});

const tableStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    paddingVertical: 3,
  },
  boldRow: {
    fontWeight: 'bold',
  },
  volumeBlock: {
    borderTop: '1px solid #e0e0e0',
    paddingTop: 8,
    marginBottom: 12,
  },
  col1: { width: 30, fontSize: 11 },
  col2: { flex: 1, fontSize: 11 },
  col3: { width: 108, fontSize: 11 },
  col4: { width: 108, fontSize: 11 },
});

export const exportVolumePdfReport = async (
  projectName: string,
  reportName: string,
  selectedVolumes: VolumeWithAsset[],
  includeOverView = true,
  progressController?: ProgressController,
) => {
  const dynamicAssetService = SkyMapAxiosServiceFactory.instance.createDynamicAssetServiceV0();
  const terrainModelService = SkyMapAxiosServiceFactory.instance.createTerrainModelServiceV0();
  const dateString = getFormatedDate();
  const logoUrl = ThemeManager.instance.isMobaTheme()
    ? (mobaLogoUrl as unknown as string)
    : (defaultLogoUrl as unknown as string);

  let overviewImageUrl: string | undefined;

  if (includeOverView) {
    const overviewPicture = await generateOverview(
      selectedVolumes,
      dynamicAssetService,
      terrainModelService,
      progressController,
    );

    if (overviewPicture) {
      const blob = new Blob([overviewPicture], { type: 'image/png' });
      overviewImageUrl = URL.createObjectURL(blob);
    }
  }

  progressController?.next();

  try {
    const component = (
      <VolumePdfReport
        dateString={dateString}
        logoUrl={logoUrl}
        overviewImageUrl={overviewImageUrl}
        projectName={projectName}
        reportName={reportName}
        selectedVolumes={selectedVolumes}
      />
    );

    const blob = await pdf(component).toBlob();
    downloadBlob(blob, `${projectName}_${dateString}`, 'pdf');
  } finally {
    if (overviewImageUrl) {
      URL.revokeObjectURL(overviewImageUrl);
    }
  }

  progressController?.next();
};
